import type { AiJob, AiJobSummary, AiReportType, AiStatus, Finplan } from '@fin/shared';
import { useEffect, useState } from 'react';
import { JobView, ShotPicker } from './Job.tsx';
import { Button, ConfirmButton, Empty, ErrorBox, Field, Icon, Modal, PageHead, Section, Segmented, Tile, useAction, useIsMobile } from '../../components/ui.tsx';
import { del, post } from '../../lib/api.ts';
import { brokerLook, isActive, jobLook, questionLook, reportLook, useAiJob } from '../../lib/ai.ts';
import { dateLong, dateShort, rub } from '../../lib/format.ts';
import { invalidate, useQuery } from '../../lib/query.ts';

type Sheet = { k: 'report'; type: AiReportType } | { k: 'question'; text?: string } | { k: 'broker' } | { k: 'job'; id: number };
type Preset = AiStatus['presets'][number];

/** «12:40» today, «23 окт» before */
function when(iso: string) {
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay ? d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : dateShort(d.toISOString().slice(0, 10));
}

function JobState({ j }: { j: AiJobSummary }) {
  if (isActive(j)) return <span className="ai-state"><span className="spinner" aria-hidden /> готовится</span>;
  if (j.status === 'failed') return <span className="ai-state neg">ошибка</span>;
  return <span className="ai-state muted">{when(j.finishedAt ?? j.createdAt)}</span>;
}

const latestFor = (jobs: AiJobSummary[] | undefined, type: AiReportType, subtitle: string) =>
  jobs?.find((j) => j.kind === 'report' && j.reportType === type && j.subtitle === subtitle);

export function AnalyticsPage() {
  const status = useQuery<AiStatus>('/ai/status');
  const jobs = useQuery<AiJobSummary[]>('/ai/jobs');
  const plan = useQuery<Finplan>('/finplan');
  const [sheet, setSheet] = useState<Sheet | null>(null);
  // the history follows a job that is still being prepared
  const busy = jobs.data?.some(isActive);
  useEffect(() => {
    if (!busy) return;
    const t = setTimeout(() => void jobs.reload(), 3000);
    return () => clearTimeout(t);
  }, [busy, jobs.data]);

  if (status.error && !status.data) return <div className="page"><ErrorBox error={status.error} onRetry={status.reload} /></div>;
  if (!status.data) return <div className="page" aria-busy="true"><span className="skeleton sk-bar" /><span className="skeleton sk-card" /></div>;
  if (!status.data.enabled) return <div className="page"><Empty>Аналитика не настроена на сервере.</Empty></div>;

  const year = new Date().getFullYear();
  const market = plan.data?.years?.find((y) => y.year === year && y.marketValue != null);
  const close = () => setSheet(null);
  return (
    <div className="page ai-page">
      <PageHead title="Аналитика" />
      {!status.data.online ? (
        <p className="ai-banner" role="status"><Icon name="question" size={18} /> Codex сейчас не на связи. Запрос подождёт его в очереди до получаса.</p>
      ) : null}

      <button type="button" className="ai-ask" onClick={() => setSheet({ k: 'question' })}>
        <Icon name="search" size={18} /><span>Спросить о своих деньгах</span>
      </button>

      <Section title="Готовые отчёты">
        <ul className="tx-list">
          {status.data.presets.map((p) => {
            const look = reportLook[p.type];
            const last = latestFor(jobs.data, p.type, p.scopes[0].subtitle);
            return (
              <li key={p.type}>
                <button type="button" className="tx" onClick={() => setSheet({ k: 'report', type: p.type })}>
                  <Tile icon={look.icon} color={look.color} />
                  <span className="tx-text"><span className="tx-title">{p.title}</span><span className="tx-sub">{p.scopes[0].subtitle}</span></span>
                  <span className="ai-row-end">{last ? <JobState j={last} /> : null}<Icon name="right" size={18} /></span>
                </button>
              </li>
            );
          })}
        </ul>
      </Section>

      <Section title="Инвестиции">
        <ul className="tx-list">
          <li>
            <button type="button" className="tx" onClick={() => setSheet({ k: 'broker' })}>
              <Tile icon={brokerLook.icon} color={brokerLook.color} />
              <span className="tx-text">
                <span className="tx-title">Портфель со скриншотов</span>
                <span className="tx-sub">{market ? <>В финплане <span className="money">{rub(market.marketValue)}</span> на {dateLong(market.marketDate ?? `${year}-12-31`)}</> : 'Стоимость портфеля и доход года для финплана'}</span>
              </span>
              <span className="ai-row-end"><Icon name="right" size={18} /></span>
            </button>
          </li>
        </ul>
      </Section>

      {jobs.data?.length ? (
        <Section title="История">
          <ul className="tx-list">
            {jobs.data.map((j) => {
              const look = jobLook(j);
              return (
                <li key={j.id}>
                  <button type="button" className="tx" onClick={() => setSheet({ k: 'job', id: j.id })}>
                    <Tile icon={look.icon} color={look.color} />
                    <span className="tx-text"><span className="tx-title">{j.title}</span><span className="tx-sub">{j.subtitle}</span></span>
                    <span className="ai-row-end"><JobState j={j} /></span>
                  </button>
                </li>
              );
            })}
          </ul>
        </Section>
      ) : null}

      <p className="muted small ai-footnote">Цифры считает трекер. Codex получает только итоги и пишет по ним текст.</p>

      {sheet?.k === 'report' ? <ReportSheet preset={status.data.presets.find((p) => p.type === sheet.type)!} jobs={jobs.data} onClose={close} /> : null}
      {sheet?.k === 'question' ? <QuestionSheet initial={sheet.text} onClose={close} /> : null}
      {sheet?.k === 'broker' ? <BrokerSheet onClose={close} /> : null}
      {sheet?.k === 'job' ? <JobSheet id={sheet.id} onClose={close} onAskAgain={(text) => setSheet({ k: 'question', text })} /> : null}
    </div>
  );
}

function ReportSheet({ preset, jobs, onClose }: { preset: Preset; jobs: AiJobSummary[] | undefined; onClose: () => void }) {
  const look = reportLook[preset.type];
  const [scope, setScope] = useState(preset.scopes[0].scope);
  const sc = preset.scopes.find((s) => s.scope === scope) ?? preset.scopes[0];
  const [jobId, setJobId] = useState<number | null>(() => latestFor(jobs, preset.type, sc.subtitle)?.id ?? null);
  const { job, error, reload } = useAiJob(jobId);
  const { busy, run } = useAction();
  const start = () => run(async () => {
    const j = await post<AiJob>('/ai/reports', { type: preset.type, scope });
    invalidate('/ai/jobs');
    setJobId(j.id);
  });
  const cancel = () => run(async () => {
    if (jobId !== null) await del(`/ai/jobs/${jobId}`);
    invalidate('/ai/jobs');
    setJobId(null);
  });
  const switchScope = (next: string) => {
    setScope(next);
    const s = preset.scopes.find((x) => x.scope === next);
    setJobId(s ? latestFor(jobs, preset.type, s.subtitle)?.id ?? null : null);
  };
  const active = job ? isActive(job) : false;
  return (
    <Modal className="modal-form" title={preset.title} onClose={onClose}
      head={preset.scopes.length > 1 ? (
        <div className="ai-head">
          <Segmented label="Какой отчёт" value={scope} onChange={switchScope} wide options={preset.scopes.map((s) => ({ value: s.scope, label: s.label }))} />
        </div>
      ) : null}
      footer={jobId === null ? (
        <><span /><Button variant="primary" busy={busy} onClick={() => void start()}>Подготовить отчёт</Button></>
      ) : active ? (
        <><span /><Button variant="ghost" busy={busy} onClick={() => void cancel()}>Отменить</Button></>
      ) : (
        <>
          <span className="muted small">{job?.finishedAt ? `Готов ${when(job.finishedAt)}` : ''}</span>
          <Button busy={busy} onClick={() => void start()}>Подготовить заново</Button>
        </>
      )}>
      <p className="ai-range">{sc.subtitle}</p>
      {jobId === null ? (
        <div className="ai-intro">
          <Tile icon={look.icon} color={look.color} size="lg" />
          <p>{look.about}</p>
        </div>
      ) : <JobView job={job} error={error} onRetry={() => { void start(); reload(); }} />}
    </Modal>
  );
}

const EXAMPLES = [
  'Сколько в месяц уходит на такси?',
  'На что трачу больше обычного?',
  'Сколько ушло в выходные?',
];

function QuestionSheet({ initial, onClose }: { initial?: string; onClose: () => void }) {
  const mobile = useIsMobile();
  const [text, setText] = useState(initial ?? '');
  const [scope, setScope] = useState<'auto' | 'month' | 'year'>('auto');
  const [jobId, setJobId] = useState<number | null>(null);
  const { job, error } = useAiJob(jobId);
  const { busy, run } = useAction();
  const ready = text.trim().length >= 3;
  const ask = () => { if (ready && !busy) void run(async () => {
    const j = await post<AiJob>('/ai/questions', { text: text.trim(), scope });
    invalidate('/ai/jobs');
    setJobId(j.id);
  }); };
  return (
    <Modal className="modal-form" title="Свой вопрос" onClose={onClose}
      footer={jobId === null ? (
        <><span /><Button variant="primary" busy={busy} disabled={!ready} onClick={ask}>Спросить</Button></>
      ) : (
        <><span /><Button disabled={job ? isActive(job) : true} onClick={() => { setJobId(null); }}>Другой вопрос</Button></>
      )}>
      {jobId === null ? (
        <div className="form">
          <Field label="Вопрос">
            <textarea className="input ai-question" rows={4} maxLength={500} value={text} autoFocus={!mobile} data-autofocus={!mobile || undefined}
              placeholder="Например: сколько я трачу на кафе по будням?" onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); ask(); } }} />
          </Field>
          <div className="chips-row ai-examples" role="group" aria-label="Примеры вопросов">
            {EXAMPLES.map((q) => <button key={q} type="button" className="chip-btn" onClick={() => setText(q)}><span>{q}</span></button>)}
          </div>
          <Field label="Если в вопросе нет срока, взять">
            <Segmented label="Срок по умолчанию" value={scope} onChange={setScope} wide
              options={[{ value: 'month', label: 'Месяц' }, { value: 'auto', label: '3 месяца' }, { value: 'year', label: 'Год' }]} />
          </Field>
          <p className="muted small">В ChatGPT уходят вопрос, названия категорий и счетов и посчитанные трекером суммы.</p>
        </div>
      ) : (
        <>
          <p className="ai-asked">{job?.params.question ?? text}</p>
          <JobView job={job} error={error} onAskAgain={(q) => { setText(q); setJobId(null); }} onRetry={ask} />
        </>
      )}
    </Modal>
  );
}

function JobSheet({ id, onClose, onAskAgain }: { id: number; onClose: () => void; onAskAgain: (text: string) => void }) {
  const [current, setCurrent] = useState(id);
  const { job, error } = useAiJob(current);
  const { busy, run } = useAction();
  const again = () => run(async () => {
    if (!job) return;
    const j = job.kind === 'report'
      ? await post<AiJob>('/ai/reports', { type: job.reportType, scope: job.params.scope })
      : await post<AiJob>('/ai/questions', { text: job.params.question, scope: job.params.scope });
    invalidate('/ai/jobs');
    setCurrent(j.id);
  });
  const remove = async () => {
    await del(`/ai/jobs/${current}`);
    invalidate('/ai/jobs');
    onClose();
  };
  const look = job ? jobLook(job) : questionLook;
  return (
    <Modal className="modal-form" title={job?.title ?? 'Ответ'} onClose={onClose}
      footer={<>
        <ConfirmButton onConfirm={remove} confirmText="Удалить ответ?">Удалить</ConfirmButton>
        {job && job.kind !== 'broker' && !isActive(job) ? <Button busy={busy} onClick={() => void again()}>{job.kind === 'report' ? 'Подготовить заново' : 'Спросить снова'}</Button> : null}
      </>}>
      <div className="ai-job-head">
        <Tile icon={look.icon} color={look.color} size="sm" />
        <span className={job?.kind === 'question' ? 'ai-asked' : 'ai-range'}>{job?.subtitle}</span>
      </div>
      <JobView job={job} error={error} onRetry={() => void again()} onAskAgain={onAskAgain} onBrokerSaved={() => invalidate('/ai/jobs')} />
    </Modal>
  );
}

/** screenshots of the broker app → the plan year's value and result, checked before saving */
export function BrokerSheet({ onClose }: { onClose: () => void }) {
  const [shots, setShots] = useState<string[]>([]);
  const [jobId, setJobId] = useState<number | null>(null);
  const { job, error } = useAiJob(jobId);
  const { busy, run } = useAction();
  const send = () => run(async () => {
    const j = await post<AiJob>('/ai/broker', { images: shots });
    invalidate('/ai/jobs');
    setJobId(j.id);
    setShots([]);
  });
  return (
    <Modal className="modal-form" title="Портфель со скриншотов" onClose={onClose}
      footer={jobId === null ? (
        <><span /><Button variant="primary" busy={busy} disabled={!shots.length} onClick={() => void send()}>Распознать</Button></>
      ) : (
        <><span /><Button disabled={job ? isActive(job) : true} onClick={() => setJobId(null)}>Другие скриншоты</Button></>
      )}>
      {jobId === null ? (
        <div className="form">
          <ShotPicker shots={shots} onChange={setShots} busy={busy} />
          <p className="muted small">Скриншоты уходят в ChatGPT через Codex и удаляются с сервера сразу после распознавания. Номера счетов не сохраняются.</p>
        </div>
      ) : <JobView job={job} error={error} onRetry={() => setJobId(null)} />}
    </Modal>
  );
}
